import { useState } from "react";
import { toast } from "sonner";
import { Link2, Check, Plus, AlertTriangle, X } from "lucide-react";
import { CtaButton, MonoLabel, SecondaryButton } from "./kit";
import api, { formatApiErrorDetail } from "../lib/api";

export const ProductLinkImport = ({ target, multi = false, onApply, onImage }) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [prod, setProd] = useState(null);
  const [importing, setImporting] = useState(null);
  const [added, setAdded] = useState([]);

  const read = async () => {
    setBusy(true); setErr(""); setProd(null); setAdded([]);
    try {
      const { data } = await api.post("/product-link/extract", { url: url.trim() });
      if (data.status !== "ok") setErr(data.message);
      else setProd({ ...data.product });
    } catch {
      setErr("I couldn't read that page — upload a photo or describe the product instead.");
    } finally {
      setBusy(false);
    }
  };

  const pick = async (src) => {
    setImporting(src);
    try {
      const { data } = await api.post("/product-link/import-image", { image_url: src, target });
      onImage(data, src);
      if (multi) setAdded((a) => [...a, src]);
      else toast("Photo imported.", { description: "It enters the pipeline like a normal upload." });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't download that image — try another one.");
    } finally {
      setImporting(null);
    }
  };

  return (
    <div data-testid="product-link-import">
      <div className="flex items-end gap-2.5 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <label className="font-mono-j text-[9.5px] tracking-[0.12em] text-[#54505B] uppercase block mb-1.5">Product from link (optional)</label>
          <input className="field-input !py-2.5" placeholder="https://yourshop.com/product…" value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && url.trim().length >= 8 && !busy) { e.preventDefault(); read(); } }}
            data-testid="pl-url-input" />
        </div>
        <SecondaryButton className="!py-2.5 !px-4 text-[13px]" onClick={read} disabled={busy || url.trim().length < 8} data-testid="pl-read-btn">
          <Link2 size={13} /> {busy ? "Reading…" : "Read page"}
        </SecondaryButton>
      </div>

      {err && (
        <p className="flex items-center gap-2 text-[12.5px] text-[#96691E] mt-2.5" data-testid="pl-error">
          <AlertTriangle size={13} className="shrink-0" /> {err}
        </p>
      )}

      {prod && (
        <div className="rounded-xl border border-[#EAE5DB] bg-[#FAF8F3] p-4 mt-3" data-testid="pl-summary-card">
          <div className="flex items-center justify-between">
            <MonoLabel>WHAT I FOUND — EDIT FREELY</MonoLabel>
            <button onClick={() => setProd(null)} className="text-[#9A9591] hover:text-[#181621]" data-testid="pl-close-btn"><X size={14} /></button>
          </div>
          <div className="grid sm:grid-cols-[1fr_140px] gap-3 mt-3">
            <input className="field-input !py-2 !text-[13px]" placeholder="Product name" value={prod.name} onChange={(e) => setProd({ ...prod, name: e.target.value })} data-testid="pl-name-input" />
            <input className="field-input !py-2 !text-[13px]" placeholder="Price / promo" value={prod.price_text} onChange={(e) => setProd({ ...prod, price_text: e.target.value })} data-testid="pl-price-input" />
          </div>
          <textarea rows={2} className="field-input !py-2 !text-[13px] w-full mt-2.5 resize-none" placeholder="Description" value={prod.description} onChange={(e) => setProd({ ...prod, description: e.target.value })} data-testid="pl-desc-input" />
          {(prod.benefits || []).length > 0 && (
            <ul className="mt-2.5 space-y-1">
              {prod.benefits.map((b, i) => (
                <li key={i} className="text-[12px] text-[#54505B] flex items-start gap-1.5" data-testid={`pl-benefit-${i}`}>
                  <Check size={12} className="text-[#3D7A55] shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>
          )}
          {(prod.images || []).length > 0 && (
            <div className="mt-3">
              <p className="font-mono-j text-[9.5px] tracking-[0.12em] text-[#54505B] uppercase mb-1.5">
                {multi ? "Photos found — add the ones you want" : "Photos found — pick your product photo"}
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="pl-image-grid">
                {prod.images.map((src, i) => (
                  <button key={src} onClick={() => pick(src)} disabled={importing !== null || added.includes(src)}
                    className={`relative shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${added.includes(src) ? "border-[#3D7A55]" : "border-[#EAE5DB] hover:border-[#181621]"}`}
                    data-testid={`pl-image-${i}`}>
                    <img src={src} alt="" className="h-20 w-20 object-cover" onError={(e) => { e.currentTarget.closest("button").style.display = "none"; }} />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/25 text-white transition-colors">
                      {importing === src ? <span className="text-[10px] font-bold bg-black/50 px-1.5 py-0.5 rounded">…</span>
                        : added.includes(src) ? <Check size={16} className="drop-shadow" />
                        : multi ? <Plus size={16} className="opacity-0 hover:opacity-100 drop-shadow" /> : null}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end mt-3">
            <CtaButton className="!py-2 !px-4 text-[13px]" onClick={() => { onApply(prod); toast("Details applied — edit anything before generating."); }} data-testid="pl-apply-btn">
              Use these details
            </CtaButton>
          </div>
        </div>
      )}
    </div>
  );
};
